/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo } from 'react';

import {
  UseUpdateCallPreferencesParams,
  UseUpdateCallPreferencesReturnType,
} from './useUpdateCallPreferences.decl';

import { convertSecondsToMinSecNotation, convertToSeconds } from '@dashboard/library';
import { usePermissions } from '@hooks/usePermissions';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useUpdateUserInformation } from '@hooks/useUpdateUserInformation/useUpdateUserInformation';
import { ICallPreferencesForm } from '@pages/user-profile-call-preferences/CallPreferencesSection/CallPreferences.section';

/**
 * Hook to update call preferences.
 * @returns an object holding a function responsible for updating call preferences, a user and the initial form values
 */
export function useUpdateCallPreferences({
  user,
}: UseUpdateCallPreferencesParams): UseUpdateCallPreferencesReturnType {
  const { track } = useTracker();

  const { updateUser } = useUpdateUserInformation(user, true);
  const { roles } = usePermissions();

  const initialFormValues = useMemo(() => {
    /* istanbul ignore next */
    const { minutes, seconds } = convertSecondsToMinSecNotation(user.wrapUpTime ?? 0);

    /* istanbul ignore next */
    return {
      useAircallPhoneNumberAsCallerID: !!user.useAircallPhoneNumberAsCallerID,
      defaultPrefix: user.defaultPrefix?.toUpperCase() ?? '',
      currentRingtone: user.currentRingtone ?? '',
      wrapUpTimeMinutes: minutes.toString(),
      wrapUpTimeSeconds: seconds.toString(),
      wrapUpTime: user.wrapUpTime ?? 0,
    };
  }, [user]);

  const onSubmit = useCallback(
    async ({ wrapUpTimeMinutes, wrapUpTimeSeconds, ...values }: ICallPreferencesForm) => {
      const wrapUpTime = convertToSeconds(parseInt(wrapUpTimeMinutes), parseInt(wrapUpTimeSeconds));
      const isWrapUpTimeEdited = wrapUpTime !== user?.wrapUpTime;

      if (isWrapUpTimeEdited) {
        track({
          event: 'wrap_up_time_edited',
          payload: {
            user_role: roles.map((item) => item.ID).toString(),
            target_user_role: user.roles.map((item) => item.ID).toString(),
            target_user_id: user.ID,
          },
        });
      }

      return updateUser({
        ...user,
        ...values,
        wrapUpTime: !Number.isNaN(wrapUpTime) ? wrapUpTime : user.wrapUpTime,
      });
    },
    [roles, track, updateUser, user]
  );

  return { user, onSubmit, initialFormValues };
}
